import React, { useState } from 'react'
import { get } from 'lodash'
import { Button, makeStyles } from '@material-ui/core'
import AdminMainLayout from './AdminMainLayout'
import { FileUploader } from '../../components/form/FileUploader'
import { showError } from '../../actions/Error'
import { loadImage, drawImageAndFrame } from '../actions/admin_commemorative_frame'

export const AdminCommemorativeFrame = () => {
  const classes = useStyles()

  const [photographFileInfo, setPhotographFileInfo] = useState(null)
  const [landscapeFrameFileInfo, setLandscapeFrameFileInfo] = useState(null)
  const [portraitFrameFileInfo, setPortraitFrameFileInfo] = useState(null)
  const [resultDataUrl, setResultDataUrl] = useState(null)
  const [resultOrientation, setResultOrientation] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [shrinkPercent, setShrinkPercent] = useState('')

  const renderPreview = (file_info) => {
    const url = get(file_info, 'download_url')
    if (!url) {
      return null
    }
    return (
      <div className={classes.preview}>
        <img className={classes.previewImg} src={url} />
      </div>
    )
  }

  const onCreate = async () => {
    const photographUrl = get(photographFileInfo, 'download_url')
    if (!photographUrl) {
      showError('Missing photograph', 'Please upload a photographer JPG first')
      return
    }

    setIsCreating(true)
    setResultDataUrl(null)

    try {
      const photographImg = await loadImage(photographUrl)

      const isPortrait = photographImg.height > photographImg.width
      const orientation = isPortrait ? 'portrait' : 'landscape'
      const frameFileInfo = isPortrait ? portraitFrameFileInfo : landscapeFrameFileInfo
      const frameUrl = get(frameFileInfo, 'download_url')

      if (!frameUrl) {
        showError(
          'Missing frame',
          `Photograph is ${orientation} — please upload a ${orientation} frame PNG`
        )
        setIsCreating(false)
        return
      }

      const frameImg = await loadImage(frameUrl)

      const onError = () => {
        showError(
          'Photograph too short for this frame',
          'The combined top and bottom frame bands are taller than the photograph'
        )
        setIsCreating(false)
      }

      const dataUrl = drawImageAndFrame(photographImg, frameImg, shrinkPercent, onError)

      setResultDataUrl(dataUrl)
      setResultOrientation(orientation)
    } catch (e) {
      showError('Failed to create commemorative frame', String(e))
    } finally {
      setIsCreating(false)
    }
  }

  const renderResult = () => {
    if (!resultDataUrl) {
      return null
    }
    return (
      <div className={classes.resultSection}>
        <h3 className={classes.resultTitle}>
          Result ({resultOrientation})
        </h3>
        <img className={classes.resultImg} src={resultDataUrl} />
        <a href={resultDataUrl} download="commemorative_frame.jpg">
          <Button variant="contained" color="primary">
            Download JPG
          </Button>
        </a>
      </div>
    )
  }

  return (
    <AdminMainLayout active_key={"commemorative_frame"}
                     title="Commemorative Frame"
                     breadcrumbs={null}>
      <div className={classes.container}>
        <h2 className={classes.pageTitle}>Commemorative Frame</h2>

        <div className={classes.sectionsRow}>
          <div className={classes.uploadSection}>
            <h3 className={classes.sectionTitle}>Photographer JPG</h3>
            <FileUploader
              onChange={(file_info) => setPhotographFileInfo(file_info)}
              onSave={null}
              fileAnnotations={null}
            />
            {renderPreview(photographFileInfo)}
          </div>

          <div className={classes.uploadSection}>
            <h3 className={classes.sectionTitle}>Landscape Frame PNG</h3>
            <FileUploader
              onChange={(file_info) => setLandscapeFrameFileInfo(file_info)}
              onSave={null}
              fileAnnotations={null}
            />
            {renderPreview(landscapeFrameFileInfo)}
          </div>

          <div className={classes.uploadSection}>
            <h3 className={classes.sectionTitle}>Portrait Frame PNG</h3>
            <FileUploader
              onChange={(file_info) => setPortraitFrameFileInfo(file_info)}
              onSave={null}
              fileAnnotations={null}
            />
            {renderPreview(portraitFrameFileInfo)}
          </div>
        </div>

        <div className={classes.percentRow}>
          <label className={classes.percentLabel}>Shrink photo by:</label>
          <input className={classes.percentInput}
                 type="number"
                 value={shrinkPercent}
                 onChange={(e) => setShrinkPercent(e.target.value)} />
          <span className={classes.percentSuffix}>%</span>
        </div>

        <div className={classes.createButtonRow}>
          <Button variant="contained"
                  color="primary"
                  disabled={isCreating}
                  onClick={onCreate}>
            {isCreating ? 'Creating...' : 'Create commemorative frame'}
          </Button>
        </div>

        {renderResult()}
      </div>
    </AdminMainLayout>
  )
}

const useStyles = makeStyles({
  container: {
    padding: 10,
  },
  pageTitle: {
    margin: '0 0 10px 0',
    fontSize: 16,
  },
  sectionsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  uploadSection: {
    border: '1px solid #ccc',
    borderRadius: 4,
    padding: 8,
    width: 240,
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: 6,
    fontSize: 13,
  },
  preview: {
    marginTop: 6,
  },
  previewImg: {
    maxWidth: 220,
    maxHeight: 160,
    border: '1px solid #eaeaea',
  },
  percentRow: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  percentLabel: {
    fontSize: 13,
  },
  percentInput: {
    width: 60,
    padding: '4px 6px',
    fontSize: 13,
    border: '1px solid #ccc',
    borderRadius: 3,
  },
  percentSuffix: {
    fontSize: 13,
  },
  createButtonRow: {
    marginTop: 16,
  },
  resultSection: {
    marginTop: 20,
    border: '1px solid #ccc',
    borderRadius: 4,
    padding: 12,
    maxWidth: 760,
  },
  resultTitle: {
    marginTop: 0,
    marginBottom: 8,
    fontSize: 14,
  },
  resultImg: {
    maxWidth: 720,
    maxHeight: 500,
    border: '1px solid #eaeaea',
    display: 'block',
    marginBottom: 10,
  },
})
